<template>
<PCHeaderBar />
<!-- :search-click="handleSearchClick" /> -->

<div class="result-page">
  <PCBreadcrumbs :breadcrumbs="breadcrumbs" :breadcrumbs:click="handleBreadcrumbsClick" />

  <div class="result-wrapper">
    <div class="search-filter-wrapper">
      <PCSearchFilter 
        ref="PCSearchFilterRef"
        :categoryList="categoryList"
        :filter="filter"
        @filter:change="handleFilterChange"
      />
    </div>
    <div class="result-listings-wrapper">
      <div class="filter-bar">
        <div class="pagination">
          <h1 class="h1-tips" v-html="h1Tips" />
          <span class="cur-page">&nbsp;{{ currentPage }}</span><span class="page-total">/{{ maxPage }}
          </span>
          <span class="arrow" @click="handlePaginateClick(-1)"><van-icon name="arrow-left" /></span>
          <span class="arrow" @click="handlePaginateClick(1)"><van-icon name="arrow" /></span>
        </div>

        <div class="filter-btn filter-btn-act" >
          <span>
            {{
              sortList.find(c => c.act)?.text 
                || (
                    priceSortAct >= 0
                      ? priceSortAct === 1
                        ? 'Low to High'
                        : 'High to Low'
                      : 'Popular'
                  )
            }}
          </span>
          
          <van-icon style="margin-left: 6px;" name="arrow-down" />
          <div class="down-content">
            <p
              v-for="(item, idx) in sortList"
              :key="idx"
              class="p_filter-item"
              :style="{ color: item.act ? '#DD3131' : ''}"
              @click="handleSortFilterClick(item)"
            >
              {{ item.text }}
            </p>
          </div>
        </div>
      </div>

      <template v-if="suggestion.keywordsSuggestions?.length">
        <p
          v-if="suggestion.suggestionUsed"
          class="correction-words-tips"
        >
          <van-icon name="info-o" color="#ff976a" />&nbsp;Showing results for&nbsp;
          <span class="first-word">{{ suggestion.keywordsSuggestions[0] }}  
          </span>.&nbsp;Search instead for&nbsp;<span class="last-word" @click="handleCorrectionWordClick(keyword || '', 1)">{{ keyword }}</span>
        </p>
        <p
          v-else
          class="correction-words-tips"
        >
          <van-icon name="info-o" />&nbsp;Showing results for&nbsp;
          <span class="first-word">{{ keyword }}</span>.&nbsp;Did you mean:
          <span class="last-word" @click="handleCorrectionWordClick(suggestion.keywordsSuggestions[0], 2)">{{ suggestion.keywordsSuggestions[0] }}</span>
        </p>  
      </template>

      <div class="listings" v-show="showResult && listings.length > 0">
        <div
          v-for="item in listings"
          :key="item.isAds ? `${item.adsIndex}_${item.listingId}` : item.listingId"
          class="listing-item"
        >
          <div class="inner-listing">
            <PCProductItemCard :productData="item" :isAds="item.isAds" />
          </div>
        </div>
      </div>

      <PCEmpty
        v-if="listings.length <= 0" 
        title-text="Your search did not match any products!"
        class="no-data"
      />

      <div class="pagination-box">
        <van-pagination :model-value="currentPage" :page-count="maxPage" :items-per-page="36" @change="handlePageClick" />
      </div>
    </div>
  </div>
</div>

<PCFooter />
</template>

<script setup lang="ts">
import { type ISearchParams } from '@/api/models/system';
import { getCtgPathByCid } from '@/api/models/product'
import type { IBreadcrumbs } from '~/components/PC/Breadcrumbs.vue';
import type { ISort } from '~/pages/search.vue';

interface ICategory { id: number, name: string, subCategories?: ICategory, bindUrl: string }
interface IPopularType extends ISort { act: boolean }
interface IProps {
  categoryList: ICategory[]
  showResult: boolean
  filter: any
  keyword: string
  listings: Search.IListing[]
  ads: Search.IAds[]
  suggestion: { suggestionUsed: boolean, keywordsSuggestions: string[] }
  maxPage: number
  pageSize: number
  h1Tips?: string
  loading?: boolean
  currentPage: number,
  sortList: ISort[],
  breadcrumbs: IBreadcrumbs[]
}

const props = withDefaults(
  defineProps<IProps>(),
  {
    maxPage: 0,
    pageSize: 36,
    currentPage: 0
  }
)
const emits = defineEmits<{
  (event: 'filter:change', params: ISearchParams): void,
  (event: 'filter:paginate', currentPage: number): void,
  (event: 'filter:sort', sortVal: number): void
  (event: 'filter:keyword', keyword: string): void
  (event: 'filter:suggestion-keyword', suggestionKeyword: string, type: 1 | 2): void
}>()

watch(
  () => props.loading,
  (val: boolean) => {
    useRequestLoading().value = val
  }
)
const router = useRouter()

const sortList = ref<IPopularType[]>(props.sortList.map(c => ({...c, act: c.text === 'Popular'})))

const handlePaginateClick = (page: number) => {  
  const changePage = props.currentPage + page

  if(changePage <= 0 || changePage > props.maxPage) {
    return
  }

  emits('filter:paginate', changePage)
}

const handleFilterChange = (p: ISearchParams) => {
  emits('filter:change', p)
}

const handleSortFilterClick = (item: IPopularType) => {
  priceSortAct.value = -1
  sortList.value.forEach((c: IPopularType) => {
    c.act = item.value === c.value
  })
  
  emits('filter:sort', item.value as number)
}

const priceSortAct = ref<number>(-1)
const PCSearchFilterRef = ref<any>()

defineExpose({
  clearFilter: () => { PCSearchFilterRef.value && PCSearchFilterRef.value.clearFilter() }
})

const handlePageClick = (e: number) => {
  emits('filter:paginate', e)
}

const handleBreadcrumbsClick = (item: any) => {
  if(/^http(s)?:\/\/.*/.test(item.path)) {
    window.location.href = item.path
  } else {
    router.push(item.path)
  }
}

const handleCorrectionWordClick = (e: string, type: 1 | 2) => {
  emits('filter:suggestion-keyword', e, type)
}
</script>

<style lang="scss" scoped>
.result-page{
  width: var(--viewport-width);
  margin: 0 auto;
  margin-bottom: 40px;
}
.loading{
  display: flex;
  align-items: center;
  margin-left: 20px;
}
.correction-words-tips{
  color: #666;
  font-size: 26px;
  line-height: 34px;
  margin: 20px;
  i{ font-size: 32px; }
  .first-word{ color: var(--default_color); }
  .last-word{ color: var(--default_color);border-bottom: 1px solid var(--default_color);cursor: pointer; }
}
.result-wrapper {
  display: flex;
  .search-filter-wrapper{
    width: 580px;
    min-width: 580px;
  }
  .result-listings-wrapper{
    flex: 1;
    margin-left: 20px;
    align-items: center;
    .filter-bar{
      padding: 20px;
      display: flex;
      font-size: 32px;
      background-color: #fff;
      margin: 0 10px 20px 10px;
      .filter-btn{
        position: relative;
        z-index: 8;
        cursor: pointer;
        margin-right: 20px;
        border-radius: 8px;
        padding: 20px 40px;
        background-color: #efefef;
        transition: all .15s;
        &:hover{
          background-color: var(--pc-default-color);
          color: #fff;
          .down-content{
            height: auto;
            padding: 20px 40px;
          }
        }
        .down-content{
          height: 0;
          transition: all .2s;
          overflow: hidden;
          position: absolute;
          background-color: #fff;
          padding: 0 40px;
          box-shadow: 0 0 6px #999;
          top: 100%;
          left: 10px;
          .p_filter-item{
            height: 60px;
            white-space: nowrap;
            line-height: 60px;
            color: #666;
            &:hover{
              color: var(--pc-default-color);
            }
          }
        }
      }
      .filter-btn-act{
        background-color: var(--pc-default-color);
        color: #fff;
      }

      .pagination{
        flex: 1;
        padding: 0 40px;
        display: flex;
        // justify-content: flex-end;
        align-items: center;
        .h1-tips{
          color: #666;
          text-overflow: ellipsis;
          text-wrap: nowrap;
        }
        .cur-page{
          color: var(--pc-default-color);
        }
        .page-total{
          color: #666;
        }
        .arrow{
          background-color: #efefef;
          margin-left: 20px;
          padding: 0 40px;
          height: 50px;
          display: inline-block;
          cursor: pointer;
          border-radius: 6px;
          @include center();
          &:hover{
            background-color: var(--pc-default-color);
            color: #fff;
          }
        }
      }
    }
  }
}
.listings{
  display: flex;
  flex-wrap: wrap;
  max-width: calc(var(--viewport-width) - 580px - 20px);
  .listing-item{
    width: 25%;
    padding: 10px;
    .inner-listing{
      background-color: #fff;
      height: 100%;
    }
  }
}
.pagination-box{
  text-align: center;
  background-color: #fff;
  margin-top: 40px
}
.no-data{
  width: auto !important;
  margin: 0 10px;
}
</style>
